// Runtime deploy config — go-live 3.1.
//
// ONE frontend image serves staging AND prod: values that differ per
// environment are injected at *container start*, not baked at build time.
// The production image's entrypoint hook (tooling/docker/
// 40-aiq-runtime-config.sh) renders /config.js from WP_* env vars;
// `index.html` loads /config.js as a classic (parser-blocking) script
// BEFORE the module bundle, so `window.__AIQ_CONFIG__` is populated before
// any module-scope config read runs.
//
// Resolution precedence everywhere a value is consumed:
//   1. window.__AIQ_CONFIG__.<key>   (runtime, per-environment)
//   2. import.meta.env.VITE_<KEY>   (build-time, dev/test ergonomics)
//   3. hard fallback                (same-origin relative paths)
//
// Local dev + unit tests + Playwright are unchanged: apps/web/public/
// config.js ships an empty object, so 2./3. apply exactly as before.

export interface AiqRuntimeConfig {
  /** Zitadel issuer, e.g. https://auth.wind-platform.com */
  oidcIssuer?: string;
  /** OIDC app client id (snowflake from Zitadel app creation). */
  oidcClientId?: string;
  /** Optional explicit redirect; defaults to `${origin}/auth/callback`. */
  oidcRedirectUri?: string;
  /** LYSE / analytics API base. Default `/api/lyse/v1` (same-origin). */
  apiBaseUrl?: string;
  /** Auth API base. Default `/api/auth/v1`. */
  authBaseUrl?: string;
  /** Assets API base. Default `/api/assets/v1`. */
  assetsBaseUrl?: string;
  /** Projects API base. Default `/api/projects/v1`. */
  projectsBaseUrl?: string;
  /** Taxonomy API base. Default `/api/taxonomy/v1`. */
  taxonomyBaseUrl?: string;
  /** Notifications API base. Default `/api/notifications/v1`. */
  notificationsBaseUrl?: string;
  /** Fleet API base (MAP-3 fleet-map aggregation). Default `/api/fleet/v1`. */
  fleetBaseUrl?: string;
  /** Dashboard API base (ONGOING-1 CX aggregates). Default `/api/dashboard/v1`. */
  dashboardBaseUrl?: string;
  /** Tenancy API base (TNCY-5 cross-tenant links). Default `/api/tenancy/v1`. */
  tenancyBaseUrl?: string;
  /** Support API base (SUP-ADMIN-UI-01 bug-report triage). Default `/api/support/v1`. */
  supportBaseUrl?: string;
  /** Time-tracking API base (TT-02 standby). Default `/api/time-tracking/v1`. */
  timeTrackingBaseUrl?: string;
  /** AFIS API base (ADM-DEV-01 unit registry). Default `/api/afis/v1`. */
  afisBaseUrl?: string;
  /** Inspection-3 API base (DI-2-3 intake). Default `/api/inspection-3/v1`. */
  inspection3BaseUrl?: string;
  /** Intake API base (DI-2-4 wiring). Default `/api/intake/v1`. */
  intakeBaseUrl?: string;
  /** Workflows API base (M3C-04 step 0). Default `/api/workflows/v1`. */
  workflowsBaseUrl?: string;
  /** MapLibre basemap style URL (MAP-1). Default tiles.wind-platform.com. */
  mapStyleUrl?: string;
  /**
   * ZV-MIS-01B — the platform-admin "Partner API sandbox" page: `"1"` /
   * `"true"` shows it, `"0"` / `"false"` hides it. Unset = the build's
   * `VITE_MISSION_SANDBOX`, else on under the dev server only. Set it on
   * staging alone: the backend routes 404 unless MISSION_SANDBOX_ENABLED.
   */
  missionSandbox?: string;
}

declare global {
  interface Window {
    __AIQ_CONFIG__?: AiqRuntimeConfig;
  }
}

/**
 * Read the runtime config injected by the production image's entrypoint.
 * Returns `{}` in non-browser contexts (Node tests) and when /config.js
 * is absent or empty (local dev) — callers then fall back to build-time
 * VITE_* values and hard defaults.
 */
export function readRuntimeConfig(): AiqRuntimeConfig {
  if (typeof window === "undefined") return {};
  const cfg = window.__AIQ_CONFIG__;
  return cfg && typeof cfg === "object" ? cfg : {};
}

/**
 * First non-empty candidate wins; empty / whitespace-only strings count
 * as unset so an entrypoint that renders `key: ""` for an unset env var
 * cannot mask the build-time fallback.
 */
export function pickConfigValue(
  ...candidates: readonly (string | undefined)[]
): string | undefined {
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim() !== "") {
      return candidate;
    }
  }
  return undefined;
}
